<template>
  <BaseModule
    v-bind="$props"
    class="container promo-list"
    data-module="promo-list"
  >
    <Loader v-if="showLoader" />

    <div
      v-else
      class="content promos"
    >
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
          :style="{ color: titleColor, fontWeight: titleWeight }"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
          :style="{ color: subtitleColor }"
        >
          {{ subtitle }}
        </h3>
      </header>

      <div class="promo-content">
        <div
          v-if="showFilter"
          class="promos__filter"
          :class="{ 'promos__filter--no-margin': !hasPromosToLoad }"
        >
          <div class="filter-item">
            <label
              class="filter-item__label"
              for="field-destino"
            >
              {{ $t('destination') }}
            </label>
            <v-select
              id="destino"
              v-model="selectedDestiny"
              class="filter-item__field"
              name="field-destino"
              label="Nome"
              :options="destiniesListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>

          <div class="filter-item">
            <label
              class="filter-item__label"
              for="field-hotel"
            >
              {{ $t('hotel') }}
            </label>
            <v-select
              id="hotel-promo"
              v-model="selectedHotel"
              class="filter-item__field"
              name="field-hotel"
              label="Nome"
              :options="hotelsListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>

          <div
            v-if="groupOption === -1"
            class="filter-item"
          >
            <label
              class="filter-item__label"
              for="field-pacote"
            >
              {{ $t('package') }}
            </label>
            <v-select
              id="pacote"
              v-model="selectedPackage"
              class="filter-item__field"
              name="field-pacote"
              label="Nome"
              :loading="loading"
              :options="packageListOptions"
              :clearable="false"
              :reduce="(opt) => opt.Codigo"
            >
              <div slot="no-options">Nenhum resultado encontrado</div>
            </v-select>
          </div>
        </div>

        <!-- Slider main container -->

        <div
          ref="swiper"
          class="promo-content__list-wrapper swiper"
        >
          <div
            v-if="hasPromos"
            class="promos__list swiper-wrapper"
            :class="{
              disabled: !isSliderEnabled,
            }"
          >
            <div
              v-for="h in promosVisibleList"
              :id="'promo-' + h.Codigo"
              :key="h.Codigo"
              class="promos__list__item swiper-slide"
            >
              <EditorLink
                class="bloco-promo"
                :model="h.UrlPromocao"
                @click.native="trackCustomEvent(h)"
              >
                <div class="bloco-promo__image">
                  <LImage
                    v-if="
                      h.Imagem &&
                      h.Imagem.Url &&
                      h.Imagem.Url.includes('https://')
                    "
                    :src="h.Imagem.Url"
                    :alt="h.Nome"
                    class="absolute-full"
                  />
                </div>
                <p
                  v-if="h.Tarifa.TemDesconto"
                  class="promo"
                >
                  {{ $tc('discount', h.Tarifa.PercentualDesconto) }}
                </p>

                <div class="infos">
                  <p class="cidade">{{ h.Cidade }}</p>
                  <p class="hotel">{{ h.Hotel }}</p>
                  <h4 class="nome">{{ h.Nome }}</h4>
                  <div
                    v-if="h.Tarifa.Valor && showValor"
                    class="diaria"
                  >
                    {{ $t('dailyRate') }}
                    <div>
                      <span
                        v-if="h.Tarifa.TemDesconto"
                        class="valor-promo"
                      >
                        {{ h.Tarifa.ValorFormatadoSemDesconto }}
                      </span>
                      <span
                        class="valor"
                        :style="{ color: getValueColor(h) }"
                      >
                        {{ h.Tarifa.ValorFormatado }}
                        <span class="slb-tooltip">
                          <ul class="items-tooltip">
                            <li>{{ h.ItensDescricao.TipoPagamento }}</li>
                            <li>{{ h.ItensDescricao.Reembolsavel }}</li>
                            <li>{{ h.ItensDescricao.Pensao }}</li>
                            <li>{{ h.ItensDescricao.MinimoNoites }}</li>
                          </ul>
                        </span>
                      </span>
                    </div>
                  </div>

                  <button class="cta">
                    {{ $t('seePromo') }}
                  </button>
                </div>
              </EditorLink>
            </div>
          </div>

          <button
            v-if="hasPromosToLoad && !isSliderEnabled"
            class="cta vazado cta--ver-mais"
            @click="showMorePromos()"
          >
            {{ $t('seeMore') }}
          </button>

          <EmptyState
            v-if="!hasPromos"
            :title="noPromoTitle"
            :subtitle="noPromoSubtitle"
            :img="promoImg"
          />

          <!-- If we need navigation buttons -->
          <div
            v-show="isSliderEnabled && promosVisibleList.length > 1"
            :class="`swiper-button-prev btn-prev-${instanceId}`"
          ></div>
          <div
            v-show="isSliderEnabled && promosVisibleList.length > 1"
            :class="`swiper-button-next btn-next-${instanceId}`"
          ></div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import vSelect from 'vue-select';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import base from '@/mixins/base';
import { pushDataLayer } from '@/helpers/gtm';
import randomizeArray from '@/helpers/randomizeArray';
import handlePromos from '@/helpers/handlePromos';
import Loader from '@/components/Loader.vue';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';
import EmptyState from './EmptyState.vue';

const emptyPromoUrlPath = require('@/assets/promoOptin.png');

export default {
  name: 'PromoList',
  components: {
    BaseModule,
    Loader,
    EditorLink,
    EmptyState,
    'v-select': vSelect,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    customNoPromoTitle: {
      type: String,
      default: '',
    },
    customNoPromoSubtitle: {
      type: String,
      default: '',
    },
    metaLandingsSelect: {
      type: Number,
      default: null,
    },
    groupOption: {
      type: Number,
      default: null,
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    isSliderEnabled: {
      type: Boolean,
      default: false,
    },
    subtitleColor: {
      type: String,
      default: '',
    },
    showValor: {
      type: Boolean,
      default: true,
    },
    orderBy: {
      type: String,
      default: 'initial-date',
    },
  },
  data() {
    return {
      loading: false,
      apiPromos: [],
      destiny: '',
      hotelCod: -1,
      selectedPackage: -1,
      visiblePromosNumber: this.showFilter ? 6 : 8,
      promoImg: emptyPromoUrlPath,
      isMobile: window.innerWidth < 640,
      packageListOptions: [],
      swiper: null,
    };
  },
  computed: {
    selectedHotel: {
      get() {
        return this.hotelCod;
      },
      set(hotelCod) {
        this.hotelCod = hotelCod;
        this.selectedPackage = -1;
      },
    },
    selectedDestiny: {
      get() {
        return this.destiny;
      },
      set(destiny) {
        this.destiny = destiny;
        this.visiblePromosNumber = 8;
        this.hotelCod = -1;
        this.selectedPackage = -1;
      },
    },
    showLoader() {
      return !this.uniquePromos.length && this.loading;
    },
    noPromoTitle() {
      return this.customNoPromoTitle || this.$t('noResults');
    },
    noPromoSubtitle() {
      return this.customNoPromoSubtitle || this.$t('noResultsSubtitle');
    },
    hasPromos() {
      return this.promosFilterList.length > 0;
    },
    enabledPromos() {
      if (this.groupOption === -1) {
        return this.apiPromos;
      }

      return this.apiPromos.filter(
        ({ CodigoGrupo }) => CodigoGrupo === this.groupOption,
      );
    },
    uniquePromos() {
      if (this.selectedPackage !== -1) {
        return this.enabledPromos.filter(
          (promo) => promo.CodigoGrupo === this.selectedPackage,
        );
      }

      const promoCodes = [];

      // filter duplicate promos by Codigo
      return this.enabledPromos.filter((promo) => {
        if (!promoCodes.includes(promo.Codigo)) {
          promoCodes.push(promo.Codigo);
          return true;
        }
        return false;
      });
    },
    promosFilterList() {
      const filteredPromos = this.uniquePromos.filter((promo) => {
        const matchesDestination =
          this.destiny !== '' ? promo.CodigoCidade === this.destiny : true;

        const matchesHotel =
          this.hotelCod !== -1 ? promo.CodigoHotel === this.hotelCod : true;

        const matchesPackage =
          this.selectedPackage !== -1
            ? promo.CodigoGrupo === this.selectedPackage
            : true;

        return matchesHotel && matchesDestination && matchesPackage;
      });

      return filteredPromos;
    },
    promosVisibleList() {
      if (this.hasPromos) {
        const orderedList = this.orderList(this.promosFilterList);
        return this.isSliderEnabled
          ? orderedList
          : orderedList.slice(0, this.visiblePromosNumber);
      }
      return [];
    },
    hasPromosToLoad() {
      return this.visiblePromosNumber < this.promosFilterList.length;
    },
    hotelsListOptions() {
      const hotelsOptions = [
        {
          Nome: this.$t('allHotels'),
          Codigo: -1,
        },
      ];

      this.enabledPromos.forEach((promo) => {
        if (
          !hotelsOptions.find((item) => item.Codigo === promo.CodigoHotel) &&
          (this.destiny === promo.CodigoCidade || this.destiny === '')
        ) {
          hotelsOptions.push({
            Nome: promo.Hotel,
            Codigo: promo.CodigoHotel,
          });
        }
      });

      return hotelsOptions;
    },
    destiniesListOptions() {
      const destinies = [
        {
          Nome: this.$t('allDestinations'),
          Codigo: '',
        },
      ];

      this.enabledPromos.forEach((promo) => {
        if (!destinies.find((city) => city.Codigo === promo.CodigoCidade)) {
          destinies.push({
            Codigo: promo.CodigoCidade,
            Nome: promo.Cidade,
          });
        }
      });

      return destinies;
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  watch: {
    metaLandingsSelect() {
      this.packageListOptions = [];
      this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
        path: 'data.groupOption',
        value: null,
      });
      this.requestPromos();
    },
    apiPromos(value) {
      const optionValue = value.length === 0 ? null : -1;

      this.$store.commit('SET_ACTIVE_EDITABLE_OBJECT_DATA', {
        path: 'data.groupOption',
        value: optionValue,
      });
    },
    showFilter() {
      this.visiblePromosNumber = this.showFilter ? 6 : 8;
    },
    loading() {
      this.swiperReset();
    },
    isSliderEnabled() {
      this.swiperReset();
    },
  },
  created() {
    this.requestPromos();
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
  },
  methods: {
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Navigation],
          observer: true,
          spaceBetween: 12,
          slidesPerView: 'auto',
          allowTouchMove: false,
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
    swiperReset() {
      if (this.swiper) {
        this.swiperDestroy();
      }

      if (this.isSliderEnabled && this.promosVisibleList.length > 1) {
        this.swiperInit();
      }
    },
    packageList() {
      const packagesOptions = [];

      if (this.apiPromos.length === 0) {
        this.packageListOptions = packagesOptions;
      } else {
        this.apiPromos.forEach((promo) => {
          if (
            !packagesOptions.find(
              (item) => item.Codigo === promo.CodigoGrupo,
            ) &&
            (this.destiny === promo.CodigoCidade || this.destiny === '') &&
            (this.hotelCod === promo.CodigoHotel || this.hotelCod === -1) &&
            promo.CodigoGrupo !== 0
          ) {
            packagesOptions.push({
              Nome: promo.NomeGrupo,
              Codigo: promo.CodigoGrupo,
            });
          }
        });

        this.packageListOptions = [
          {
            Nome: this.$t('allPackages'),
            Codigo: -1,
          },
          ...packagesOptions,
        ];
      }
    },
    orderList(list) {
      const promoList = [...list];

      switch (this.orderBy) {
        case 'initial-date':
          return promoList.sort((promoA, promoB) => {
            const date1 = promoA.PeriodoVigente.Inicio;
            const date2 = promoB.PeriodoVigente.Inicio;

            if (!date1) {
              return 1;
            }
            if (!date2) {
              return -1;
            }

            return new Date(date1) - new Date(date2);
          });
        case 'lowest-price':
          return promoList.sort(
            (promoA, promoB) => promoA.Tarifa.Valor - promoB.Tarifa.Valor,
          );
        case 'highest-price':
          return promoList.sort(
            (promoA, promoB) => promoB.Tarifa.Valor - promoA.Tarifa.Valor,
          );
        case 'highest-discount':
          return promoList.sort((promoA, promoB) => {
            const discount1 = this.hasPromoDiscount(promoA)
              ? promoA.Tarifa.PercentualDesconto
              : 0;
            const discount2 = this.hasPromoDiscount(promoB)
              ? promoB.Tarifa.PercentualDesconto
              : 0;

            return discount2 - discount1;
          });
        case 'random':
          return randomizeArray(promoList);
        default:
          return promoList;
      }
    },
    hasPromoDiscount(promo) {
      const tarifa = promo.Tarifa;
      return (
        tarifa.ExibirDesconto &&
        tarifa.TemDesconto &&
        tarifa.PercentualDesconto !== 0
      );
    },
    getValueColor(promo) {
      return promo.Tarifa.TemDesconto
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    showMorePromos() {
      this.visiblePromosNumber += this.showFilter ? 6 : 8;
    },
    async requestPromos() {
      this.loading = true;

      let { letsbookLandingPageId } = this.settings;

      if (this.metaLandingsSelect !== null) {
        letsbookLandingPageId = this.metaLandingsSelect;
      }
      const platform = this.isMobile ? 'mobile' : 'desktop';
      const url = `${this.settings.letsbookApiId}/promocoesLanding/${letsbookLandingPageId}/${platform}/${this.apiLocale}`;

      if (!this.apiData[url]) {
        try {
          await this.$store.dispatch('loadApiData', url);
        } catch {
          return;
        }
      }

      this.apiPromos = await handlePromos(this.apiData[url]);
      this.packageList();

      this.loading = false;
    },
    randomize(promoArray) {
      const array = promoArray;
      let currentIndex = promoArray.length;
      let randomIndex;

      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        [array[currentIndex], array[randomIndex]] = [
          array[randomIndex],
          array[currentIndex],
        ];
      }
      return array;
    },
    trackCustomEvent(promo) {
      pushDataLayer({
        event: 'select_hotel_promotion',
        hotel_id: String(promo.CodigoHotel),
        hotel_name: promo.Hotel,
        promotion_name: promo.Nome,
        price_starting: promo.Tarifa.Valor,
      });
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'title',
                maxlength: 40,
              },
              {
                name: 'subtitle',
                maxlength: 80,
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'metaLandingsSelect',
                type: 'SelectLandingPageId',
                label: 'Defina o agrupador de hotéis',
              },
              {
                name: 'groupOption',
                type: 'VueSelect',
                label: 'Defina o agrupador de promoções',
                options: {
                  watch: true,
                  attribute: 'packageListOptions',
                  handler(packageListOptions) {
                    return packageListOptions.map((option) => ({
                      label: option.Nome,
                      value: option.Codigo,
                    }));
                  },
                },
                isEnabled({ metaLandingsSelect }) {
                  return metaLandingsSelect;
                },
              },
              {
                name: 'showFilter',
                type: 'Checkbox',
                label: 'Mostrar filtro',
              },
              {
                name: 'orderBy',
                type: 'VueSelect',
                label: 'Ordenar promoções por:',
                options: [
                  {
                    label: 'Data de início',
                    value: 'initial-date',
                  },
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                  {
                    label: 'Ordem aleatória',
                    value: 'random',
                  },
                ],
              },
              {
                name: 'showValor',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
              {
                name: 'isSliderEnabled',
                type: 'Checkbox',
                label: 'Ativar deslizamento dos itens',
                helpText: 'Ativa o deslizamento dos itens como carrossel.',
              },
            ],
          },
          {
            name: 'emptyState',
            label: 'Aviso de promoção indisponível',
            helpText:
              'Defina uma mensagem que aparecerá para os seus hóspedes quando não houver nenhuma promoção cadastrada.',
            fields: [
              {
                name: 'customNoPromoTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'customNoPromoSubtitle',
                type: 'LongText',
                label: 'Subtítulo',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'subtitleColor',
                after: 'titleColor',
                type: 'ColorPicker',
                label: 'Cor do subtítulo',
              },
            ],
            toRemove: ['textColor'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        hotel: 'Hotel',
        destination: 'Destino',
        package: 'Pacote promocional',
        allHotels: 'Todos os hotéis',
        allDestinations: 'Todos os destinos',
        allPackages: 'Todos os pacotes',
        discount: 'Até {count}% OFF',
        dailyRate: 'Diárias a partir de',
        seePromo: 'Ver mais',
        seeMore: 'Ver mais',
        noResults: 'Ops, nossas promoções esgotaram!',
        noResultsSubtitle:
          'Mas fique atento que em breve teremos novas ofertas para você aproveitar.',
      },
      en: {
        hotel: 'Hotel',
        destination: 'Destination',
        package: 'Promotional package',
        allHotels: 'All hotels',
        allDestinations: 'All destinations',
        allPackages: 'All packages',
        discount: 'Up to {count}% OFF',
        dailyRate: 'Room rates from',
        seePromo: 'See more',
        seeMore: 'See more',
        noResults: 'Ops, our promotions are over!',
        noResultsSubtitle:
          "Stay tuned that we'll soon have new offers for you.",
      },
      es: {
        hotel: 'Hotel',
        destination: 'Destino',
        package: 'Paquete promocional',
        allHotels: 'Todos los hoteles',
        allDestinations: 'Todos los destinos',
        allPackages: 'Todos los paquetes',
        discount: 'Hasta {count}% OFF',
        dailyRate: 'Tarifas a partir de',
        seePromo: 'Ver más',
        seeMore: 'Ver más',
        noResults: 'Ops, nuestras promociones están agotadas.',
        noResultsSubtitle:
          'Estén atentos que pronto tendremos nuevas ofertas para usted.',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/base/mixins.scss';

.content {
  max-width: 1300px !important;
}

.promo-list {
  text-align: center;
  max-width: 100%;
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  ::v-deep {
    .v-select {
      width: 100% !important;
      font-size: $size-normal !important;
      padding: 16px 8px !important;

      input {
        color: $lb-medium-gray;
      }

      .vs__dropdown-toggle {
        border: none;
        padding: 0;

        .vs__selected-options {
          max-width: calc(100% - 23px);

          input {
            margin: 0;
            padding: 0;
          }
        }

        .vs__selected {
          width: calc(100% - 10px);
          display: block;
          margin: 0;
          padding: 0;
          color: $lb-medium-gray;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .vs__dropdown-option {
        color: $lb-medium-gray;
        font-size: $size-normal;
        line-height: 1.2;
        white-space: unset;
        padding: 6px 20px;

        &--highlight {
          color: $lb-white;
        }
      }
    }

    .empty-state {
      max-width: none;

      .content {
        flex-direction: column;

        .image {
          img {
            width: 145px;
          }
        }

        .text {
          margin: 0;
          text-align: center;

          .tit {
            font-size: 24px;
            line-height: 1.4;
          }
        }
      }
    }
  }

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
    max-width: none;
  }

  .promo-content {
    display: flex;
    align-items: flex-start;
    padding-top: 16px;

    &__list-wrapper {
      width: 100%;
    }

    @include media-breakpoint-down(md) {
      flex-direction: column;
      padding-top: 0;
    }

    .promos {
      &__filter {
        padding: 24px 12px;
        background: $lb-white;
        min-width: 280px;
        margin-left: 20px;
        margin-right: 20px;
        margin-bottom: 74px;
        min-height: 468px;
        position: sticky;
        margin-top: auto;
        bottom: 24px;
        border-radius: 8px;

        &--no-margin {
          margin-bottom: 0;
        }

        @include media-breakpoint-down(md) {
          width: calc(100% - 40px);
          margin: 0 20px 40px;
          min-height: unset;
          position: static;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          & + .filter-item {
            margin-top: 24px;

            @include media-breakpoint-down(md) {
              margin-top: 12px;
            }
          }

          @include media-breakpoint-down(md) {
            width: 100%;
            margin: 10px 0;
          }

          @include media-breakpoint-down(lg) {
            display: block;
            text-align: left;
            width: 100%;
            margin: 0;

            .v-select {
              margin-top: 4px;
            }
          }

          &__label {
            font-size: $size-normal;
            font-style: normal;
            font-weight: $lb-semibold;
            line-height: 21px;
            letter-spacing: -0.01em;
            text-align: center;
            color: $gray-100;
            margin-bottom: 10px;
            align-items: center;
            display: inline-block;

            @include media-breakpoint-down(md) {
              margin-bottom: 0;
            }
          }

          &__field {
            padding: 15px 20px;
            border: 1px solid #b4b4b4 !important;
            border: 0;
            font-size: 18px;
            font-weight: $lb-light;
            letter-spacing: -0.01em;
            text-align: center;
            color: $lb-dark-gray;
            align-items: center;
            border-radius: 5px;

            @include media-breakpoint-down(lg) {
              max-width: 100%;
            }
          }
        }
      }

      &__list {
        @include media-breakpoint-up(md) {
          display: flex;
          flex-grow: 1;
          margin: -12px;
        }

        @include media-breakpoint-down(md) {
          width: 100%;
        }

        &__item {
          box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
          position: relative;
          margin-bottom: 24px;
          width: 100%;
          max-width: 280px;
          border-radius: 8px;

          @include media-breakpoint-up(md) {
            margin: 12px;
          }
        }
      }
    }

    .swiper {
      overflow: hidden;
      padding: 20px;
      margin: -20px 0;

      .swiper-wrapper {
        &.disabled {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;

          .swiper-slide {
            margin-right: 0 !important;
          }
        }
      }

      .swiper-button-prev,
      .swiper-button-next {
        width: 60px;
        position: absolute;

        &::after {
          background-color: rgba($lb-black, 0.333);
          color: $lb-white;
          border-radius: 50px;
          width: 50px;
          height: 50px;
          font-size: 30px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        &.swiper-button-disabled {
          display: none;
        }
      }
    }
  }

  .bloco-promo {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
    transform: scale(1);
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease-out;
    background-color: $lb-white;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      box-shadow: -6px 6px 12px -3px rgba($lb-black, 0.2);
      transform: scale(1.02);
      text-decoration: none;

      .cta {
        background-color: $primary-140;

        &::after {
          transform: scaleX(0) !important;
        }
      }
    }

    &__image {
      position: relative;
      width: 100%;

      &::before {
        content: '';
        display: block;
        padding-bottom: 75%;
      }

      .img {
        border-radius: 8px;
      }
    }

    .infos {
      display: flex;
      flex-direction: column;
      padding: 16px;
      text-align: left;

      .cta {
        margin-top: 15px;
        width: 100%;
      }
    }

    .promo {
      color: $lb-white;
      background-color: var(--promo-color);
      padding: 7px 16px;
      line-height: 1;
      position: absolute;
      top: -15px;
      left: 24px;
      z-index: 50;
      font-weight: $lb-medium;
      letter-spacing: -0.005em;
      margin-bottom: 0;
      border-radius: 8px;
    }

    .cidade {
      align-self: flex-start;
      font-size: $size-small;
      font-weight: $lb-semibold;
      color: $gray-90;
      background-color: #f3f3f3;
      padding: 8px 16px;
      border-radius: 24px;
    }

    .hotel {
      color: $gray-90;
      font-size: $size-small;
      margin-top: 8px;
      min-height: 2.4em;
      line-height: 1.2;
    }

    .nome {
      color: $gray-90;
      margin-top: 16px;
      margin-bottom: 0;
      font-size: $size-normal;
      font-weight: $lb-bold;
      min-height: 2.4em;
      line-height: 1.2;
    }

    .diaria {
      color: $lb-gray;
      margin-top: 16px;
    }

    .valor {
      font-size: 24px;
      font-weight: $lb-bold;
      text-transform: uppercase;
      position: relative;

      .slb-tooltip {
        visibility: hidden;
        opacity: 0;
      }

      &:hover {
        .slb-tooltip {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
      }
    }

    .valor-promo {
      text-transform: uppercase;
      text-decoration: line-through;
      display: inline-block;
      margin-right: 5px;
    }

    .cta {
      width: 120px;
      font-weight: $lb-bold;
      white-space: nowrap;
      text-decoration: none;
      border-radius: 8px;
      transition: 0.2s;

      &__label {
        padding: 0;
      }
    }
  }

  .cta {
    &--ver-mais {
      text-transform: uppercase;
      margin: auto;
      width: 100%;
      color: var(--secondary-color);
      border-color: var(--secondary-color);
      margin-top: 24px;

      @include media-breakpoint-up(md) {
        width: 200px;
      }
    }
  }
}
</style>
